import { Link } from "react-router-dom";
import { ArrowUpRight, type LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/shared/lib/cn";
import { motionTokens, riseItem, springs, staggerContainer } from "@/shared/lib/motion";

/** `green` : tuile mise en avant (fond vert profond), les autres sur un dégradé clair de leur teinte. */
export type QuickAccessTone = "green" | "blue" | "orange" | "purple" | "mint" | "red";

export type DashboardQuickAccessConfig = {
  icon: LucideIcon;
  tone: QuickAccessTone;
};

export type DashboardQuickAccessItem = {
  id: string;
  href: string;
  label: string;
};

type DashboardQuickAccessProps = {
  items: DashboardQuickAccessItem[];
  configById: Record<string, DashboardQuickAccessConfig>;
  activeId?: string;
  onSelect?: (id: string) => void;
  className?: string;
};

/** Mêmes teintes que les grandes tuiles du tableau de bord Assurance (Safecheck-assureur). */
const TONES: Record<QuickAccessTone, { card: string; chip: string; label: string; arrow: string; watermark: string; ring: string }> = {
  green: {
    card: "border-transparent bg-[linear-gradient(135deg,#0a4a31_0%,#0e6a40_50%,#15a34a_100%)] text-white",
    chip: "bg-accent text-accent-foreground",
    label: "text-white",
    arrow: "text-white/80 group-hover:text-accent",
    watermark: "text-white/10",
    ring: "ring-accent",
  },
  blue: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f4f8ff_55%,#e6effd_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#14283d_100%)]",
    chip: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300",
    label: "text-foreground",
    arrow: "text-muted-foreground group-hover:text-blue-600",
    watermark: "text-blue-200 dark:text-blue-500/20",
    ring: "ring-blue-400/60",
  },
  orange: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#fff6ef_55%,#ffeadb_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#2e2219_100%)]",
    chip: "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300",
    label: "text-foreground",
    arrow: "text-muted-foreground group-hover:text-orange-600",
    watermark: "text-orange-200 dark:text-orange-500/20",
    ring: "ring-orange-400/60",
  },
  purple: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f7f3ff_55%,#ece2fc_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#2c2244_100%)]",
    chip: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
    label: "text-foreground",
    arrow: "text-muted-foreground group-hover:text-violet-600",
    watermark: "text-violet-200 dark:text-violet-500/20",
    ring: "ring-violet-400/60",
  },
  mint: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#f0fbf5_55%,#d5f5e3_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#14382f_100%)]",
    chip: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
    label: "text-foreground",
    arrow: "text-muted-foreground group-hover:text-emerald-600",
    watermark: "text-emerald-200 dark:text-emerald-500/20",
    ring: "ring-emerald-400/60",
  },
  red: {
    card: "border-border bg-[linear-gradient(135deg,#ffffff_0%,#fff4f4_55%,#fbdcdc_100%)] dark:bg-[linear-gradient(135deg,#132a2f_0%,#3d1d20_100%)]",
    chip: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300",
    label: "text-foreground",
    arrow: "text-muted-foreground group-hover:text-red-600",
    watermark: "text-red-200 dark:text-red-500/20",
    ring: "ring-red-400/60",
  },
};

export function DashboardQuickAccess({
  items,
  configById,
  activeId,
  onSelect,
  className,
}: DashboardQuickAccessProps) {
  return (
    <motion.div
      className={cn("grid grid-cols-2 gap-4 md:grid-cols-4", className)}
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
    >
      {items.map((item) => {
        const config = configById[item.id];
        if (!config) return null;

        const Icon = config.icon;
        const styles = TONES[config.tone];
        const isActive = activeId === item.id;

        return (
          <motion.div
            key={item.id}
            variants={riseItem}
            whileHover={{ y: -motionTokens.distance.xs }}
            transition={springs.snappy}
            className={cn(
              "relative isolate overflow-hidden rounded-[var(--radius-card)] border shadow-[var(--shadow-card)]",
              styles.card,
              isActive && cn("ring-2 ring-offset-2 ring-offset-background", styles.ring),
            )}
          >
            {config.tone === "green" ? (
              // Reflets clairs en diagonale et lueur lime, comme la tuile « À payer » d'origine.
              <span
                className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(115deg,transparent_45%,rgb(255_255_255/0.08)_55%,transparent_65%),radial-gradient(circle_at_85%_20%,rgb(166_242_27/0.25),transparent_45%)]"
                aria-hidden="true"
              />
            ) : null}
            <Icon
              size={72}
              strokeWidth={1.25}
              className={cn("pointer-events-none absolute -bottom-3 -right-2 -z-10", styles.watermark)}
              aria-hidden="true"
            />
            <Link
              to={item.href}
              onClick={() => onSelect?.(item.id)}
              aria-current={isActive ? "page" : undefined}
              className="group flex min-h-[128px] flex-col justify-between gap-4 p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <span className="flex items-start justify-between gap-3">
                <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", styles.chip)}>
                  <Icon size={19} strokeWidth={2} aria-hidden="true" />
                </span>
                <ArrowUpRight
                  size={18}
                  className={cn("shrink-0 transition-[color,transform] duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5", styles.arrow)}
                  aria-hidden="true"
                />
              </span>
              <span className={cn("text-[15px] font-semibold leading-snug", styles.label)}>{item.label}</span>
            </Link>
          </motion.div>
        );
      })}
    </motion.div>
  );
}
